/**
 * Page /associations-annuaire — annuaire citoyen des associations.
 *
 * Accessible à tout administré. Liste + fiche publique, suivre / ne plus suivre,
 * demander à rejoindre (rattachement), voir les annonces. Le bureau d'une asso
 * peut publier une annonce à ses abonnés depuis la fiche.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { AppHeader } from '@/components/AppHeader';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { ActeursLocauxSection } from '@/components/administre/ActeursLocauxSection';
import { Users, Search, Bell, BellOff, UserPlus, Megaphone, Mail, Phone, MapPin, Globe } from 'lucide-react';
import type { AssociationAnnuaireItem, AssociationAnnonce } from '@roronoa-collectivites/shared-types';
import { ASSOCIATION_DOMAINES } from '@roronoa-collectivites/shared-types';

export function AnnuaireAssociationsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState<AssociationAnnuaireItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [domaine, setDomaine] = useState('');

  const [fiche, setFiche] = useState<AssociationAnnuaireItem | null>(null);
  const [annonces, setAnnonces] = useState<AssociationAnnonce[]>([]);
  const [busy, setBusy] = useState(false);
  // publication d'annonce (bureau)
  const [titre, setTitre] = useState('');
  const [corps, setCorps] = useState('');

  async function charger() {
    setError(null);
    try { setItems(await api.associations.annuaire.list(recherche.trim() || undefined, domaine || undefined)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
  }
  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // Recharge automatiquement quand on change le filtre domaine.
  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [domaine]);

  async function ouvrirFiche(it: AssociationAnnuaireItem) {
    setFiche(it); setAnnonces([]); setTitre(''); setCorps('');
    try { setAnnonces(await api.associations.annuaire.annonces(it.id)); } catch { /* ignore */ }
  }
  async function rafraichirFiche(id: string) {
    try { setFiche(await api.associations.annuaire.fiche(id)); await charger(); } catch { /* ignore */ }
  }
  async function toggleAbo(it: AssociationAnnuaireItem) {
    setBusy(true);
    try {
      const r = await api.associations.annuaire.toggleAbonnement(it.id);
      toast.success(r.abonne ? 'Vous suivez cette association' : 'Vous ne suivez plus cette association');
      await rafraichirFiche(it.id);
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }
  async function demanderRejoindre(it: AssociationAnnuaireItem) {
    setBusy(true);
    try {
      await api.associations.demanderRattachement(it.id, 'membre');
      toast.success('Demande de rattachement envoyée à la mairie');
      await rafraichirFiche(it.id);
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }
  async function publier(it: AssociationAnnuaireItem) {
    if (!titre.trim() || !corps.trim()) { toast.error('Titre et message requis.'); return; }
    setBusy(true);
    try {
      await api.associations.annuaire.publierAnnonce(it.id, { titre: titre.trim(), corps: corps.trim() });
      toast.success('Annonce publiée et envoyée aux abonnés');
      setTitre(''); setCorps('');
      setAnnonces(await api.associations.annuaire.annonces(it.id));
    } catch (e) { toast.error(e instanceof ApiException ? e.message : 'Erreur'); } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Association / Acteurs locaux" accent="asso" />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LogoMark icon={Users} accent="asso" size="lg" />
            <div>
              <SectionEyebrow as="div">Vie associative</SectionEyebrow>
              <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Associations de la commune</h1>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/mon-association')}>Mon espace</Button>
        </div>

        {error && <Alert variant="error" className="mb-3">{error}</Alert>}

        <div className="mb-4 flex flex-wrap gap-2">
          <Input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher une association…"
            onKeyDown={(e) => { if (e.key === 'Enter') charger(); }} />
          <select
            className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
            value={domaine}
            onChange={(e) => { setDomaine(e.target.value); }}
            aria-label="Filtrer par domaine">
            <option value="">Tous les domaines</option>
            {ASSOCIATION_DOMAINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <Button variant="secondary" onClick={charger}><Search className="h-4 w-4" /> Rechercher</Button>
        </div>

        {items === null ? <p className="text-sm text-ink-muted">Chargement…</p> : items.length === 0 ? (
          <Card><div className="p-8 text-center text-sm text-ink-muted">Aucune association publiée pour le moment.</div></Card>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <Card key={it.id}>
                <button type="button" onClick={() => ouvrirFiche(it)} className="flex w-full items-center justify-between gap-3 p-3 text-left transition-colors hover:bg-surface/60">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{it.nom}</div>
                    <div className="text-xs text-ink-subtle">{it.domaine}{it.ville ? ` · ${it.ville}` : ''} · {it.nbAbonnes} abonné{it.nbAbonnes > 1 ? 's' : ''}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {it.estRepresentant && <Badge variant="info">membre</Badge>}
                    {it.abonne && <Badge variant="success">suivi</Badge>}
                  </div>
                </button>
              </Card>
            ))}
          </div>
        )}

        {/* ===== Acteurs locaux & producteurs (migration 131) ===== */}
        <section className="mt-10">
          <SectionEyebrow>Près de chez vous</SectionEyebrow>
          <h2 className="mt-1 font-display text-lg font-semibold tracking-r-tight text-ink sm:text-xl">
            Commerces, artisans & producteurs
          </h2>
          <p className="mt-1.5 text-sm text-ink-muted">
            Les acteurs économiques de la commune. Les producteurs locaux proposent leurs paniers et produits en circuit court.
          </p>
          <div className="mt-4">
            <ActeursLocauxSection />
          </div>
        </section>
      </main>

      {/* Fiche */}
      <Dialog open={!!fiche} onClose={() => setFiche(null)} title={fiche?.nom ?? 'Association'} size="md">
        {fiche && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="info">{fiche.domaine}</Badge>
              {fiche.estRepresentant && <Badge variant="success">vous êtes membre</Badge>}
              {fiche.abonne && <Badge variant="success">vous suivez</Badge>}
            </div>
            {fiche.description && <p className="text-sm text-ink-muted">{fiche.description}</p>}
            <div className="space-y-1 text-sm text-ink-muted">
              {fiche.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {fiche.email}</div>}
              {fiche.telephone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {fiche.telephone}</div>}
              {(fiche.adresse || fiche.ville) && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {[fiche.adresse, fiche.ville].filter(Boolean).join(', ')}</div>}
              {fiche.siteWeb && <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> {fiche.siteWeb}</div>}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border pt-3">
              <Button variant={fiche.abonne ? 'ghost' : 'asso'} size="sm" onClick={() => toggleAbo(fiche)} disabled={busy}>
                {fiche.abonne ? <><BellOff className="h-4 w-4" /> Ne plus suivre</> : <><Bell className="h-4 w-4" /> Suivre</>}
              </Button>
              {!fiche.estRepresentant && !fiche.demandeEnAttente && (
                <Button variant="secondary" size="sm" onClick={() => demanderRejoindre(fiche)} disabled={busy}>
                  <UserPlus className="h-4 w-4" /> Demander à rejoindre
                </Button>
              )}
              {fiche.demandeEnAttente && <Badge variant="warning">demande en attente</Badge>}
            </div>

            {/* Annonces */}
            <div className="border-t border-border pt-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-ink"><Megaphone className="h-4 w-4" /> Annonces</div>
              {annonces.length === 0 ? <div className="text-xs text-ink-subtle">Aucune annonce.</div> : (
                <div className="space-y-2">
                  {annonces.map((a) => (
                    <div key={a.id} className="rounded-r-md border border-border bg-surface/40 p-2.5">
                      <div className="text-sm font-medium text-ink">{a.titre}</div>
                      <div className="mt-0.5 whitespace-pre-wrap text-xs text-ink-muted">{a.corps}</div>
                      {a.dateEvent && <div className="mt-1 text-[11px] text-ink-subtle">📅 {new Date(a.dateEvent).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}</div>}
                      {a.lieu && <div className="text-[11px] text-ink-subtle">📍 {a.lieu}</div>}
                      {a.horaires && <div className="text-[11px] text-ink-subtle">🕒 {a.horaires}</div>}
                      {a.imageUrl && <img src={a.imageUrl} alt={a.titre} loading="lazy" className="mt-2 max-h-60 w-full rounded-r-md object-contain" />}
                      <div className="mt-1 text-[11px] text-ink-subtle">{new Date(a.createdAt).toLocaleDateString('fr-FR')}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Publication réservée au bureau ou aux membres explicitement
                  autorisés à publier. Un simple membre ne voit pas le formulaire. */}
              {fiche.peutPublier && (
                <div className="mt-3 space-y-2 rounded-r-md border border-dashed border-border p-2.5">
                  <div className="text-xs font-medium text-ink-muted">Publier une annonce (réservé au bureau)</div>
                  <Field label="Titre" htmlFor="an-titre"><Input id="an-titre" value={titre} onChange={(e) => setTitre(e.target.value)} /></Field>
                  <Field label="Message" htmlFor="an-corps"><Input id="an-corps" value={corps} onChange={(e) => setCorps(e.target.value)} /></Field>
                  <div className="flex justify-end"><Button variant="asso" size="sm" onClick={() => publier(fiche)} disabled={busy}>Publier aux abonnés</Button></div>
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
